import { useState } from "react";

import { UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClientQuery } from "client/accounts/hooks";
import AddressSelectPanel from "client/accounts/components/AddressSelectPanel";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useCreateAddressDrawer } from "client/accounts/stackables";

import { DetailSubscription } from "../../types";
import { useInvalidateSubscriptions } from "../../hooks/invalidate";
import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "../../hooks";
import { isSubscriptionPaymentUnchargeable } from "../../utils";
import SubscriptionPaymentAlert from "../SubscriptionPaymentAlert";

export type SubscriptionAddressUpdateDrawerProps = {
  id: number;
};

type SubscriptionAddressUpdateFormProps = {
  subscription: DetailSubscription;
};

const SubscriptionAddressUpdateForm = ({ subscription }: SubscriptionAddressUpdateFormProps) => {
  const isPaymentUnchargeable = isSubscriptionPaymentUnchargeable(subscription);
  const { close } = useCurrentDrawer();
  const { open: openCreateAddressDrawer } = useCreateAddressDrawer();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();

  const [addressId, setAddressId] = useState(subscription.shipping_address?.id);

  const invalidateSubscriptions = useInvalidateSubscriptions();

  const {
    mutate: updateSubscription,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: () => {
      invalidateSubscriptions();
      close();
    },
  });
  if (isClientLoading) {
    return <LoadingContainer />;
  }

  if (!client || clientError) {
    return <FetchErrorContainer entityName="account" error={clientError} />;
  }

  const { addresses } = client;
  const selectedAddress = addresses.find((address) => addressId === address.id);

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update address"
        description="This will update shipping address used for this Autoship. All other Autoships will not be affected."
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={!isPaymentUnchargeable}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Modify,
            shipping_address_id: addressId,
          })
        }
        submitText="Update Address"
        isLoading={isUpdating}
        error={updateError}
        fieldErrors={[updateError?.errors.shipping_address_id]}
      >
        {isPaymentUnchargeable && (
          <DrawerLayout.Alerts>
            <SubscriptionPaymentAlert subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        <AddressSelectPanel
          options={addresses}
          value={selectedAddress}
          onClick={(address) => setAddressId(address.id)}
          onAdd={() =>
            openCreateAddressDrawer({
              onSuccess: (address) => setAddressId(address.id),
            })
          }
        />
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionAddressUpdateDrawer = ({ id }: SubscriptionAddressUpdateDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionAddressUpdateForm subscription={subscription} />;
};

export default SubscriptionAddressUpdateDrawer;
